import { doc, getDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db } from './firebase';
import { DeliveryRule } from './deliveryConfigService';

export interface DeliveryRulesConfig {
  rules: DeliveryRule[];
  maxClientDistanceKm?: number;
  enableAutoFreeDelivery?: boolean;
  freeDeliveryMinOrderAmount?: number;
  maxFreeDeliveryKm?: number;
  groupId?: string | null;
}

const defaultRulesConfig: DeliveryRulesConfig = {
  maxClientDistanceKm: 6,
  enableAutoFreeDelivery: false,
  freeDeliveryMinOrderAmount: undefined,
  maxFreeDeliveryKm: 6,
  rules: [
    { fee: 10, maxKm: 6, minKm: 0 },
    { fee: 15, maxKm: 9, minKm: 6.01 },
    { fee: 20, maxKm: 15, minKm: 9.01 },
  ],
};

const buildScopedConfig = (
  data: Record<string, any> | undefined,
  groupId?: string | null
): DeliveryRulesConfig => {
  if (!data) {
    return { ...defaultRulesConfig, groupId: groupId || null };
  }

  return {
    rules: Array.isArray(data.rules) ? data.rules : defaultRulesConfig.rules,
    maxClientDistanceKm:
      typeof data.maxClientDistanceKm === 'number'
        ? data.maxClientDistanceKm
        : defaultRulesConfig.maxClientDistanceKm,
    enableAutoFreeDelivery:
      typeof data.enableAutoFreeDelivery === 'boolean'
        ? data.enableAutoFreeDelivery
        : defaultRulesConfig.enableAutoFreeDelivery,
    freeDeliveryMinOrderAmount:
      typeof data.freeDeliveryMinOrderAmount === 'number'
        ? data.freeDeliveryMinOrderAmount
        : undefined,
    maxFreeDeliveryKm:
      typeof data.maxFreeDeliveryKm === 'number'
        ? data.maxFreeDeliveryKm
        : defaultRulesConfig.maxFreeDeliveryKm,
    groupId: groupId || null,
  };
};

export const getDeliveryRules = async (groupId?: string | null): Promise<DeliveryRulesConfig> => {
  try {
    if (groupId) {
      const scopedDoc = await getDoc(doc(db, 'delivery_rules', groupId));
      if (scopedDoc.exists()) {
        return buildScopedConfig(scopedDoc.data() as Record<string, any>, groupId);
      }
    }

    return { ...defaultRulesConfig, groupId: groupId || null };
  } catch (error) {
    console.error('Erreur lors de la récupération des règles de livraison:', error);
    throw error;
  }
};

export const updateDeliveryRules = async (
  groupId: string | null | undefined,
  rulesConfig: DeliveryRulesConfig
): Promise<DeliveryRulesConfig> => {
  try {
    if (!groupId) {
      throw new Error('Aucun groupe actif sélectionné pour enregistrer les règles de livraison.');
    }

    const sortedRules = [...rulesConfig.rules].sort((a, b) => a.minKm - b.minKm);

    let maxClientDistanceKm: number | undefined = undefined;
    if (rulesConfig.maxClientDistanceKm !== undefined) {
      const parsed = Number(rulesConfig.maxClientDistanceKm);
      if (!isNaN(parsed) && parsed > 0) {
        maxClientDistanceKm = parsed;
      }
    }

    let maxFreeDeliveryKm: number | undefined = undefined;
    if (rulesConfig.maxFreeDeliveryKm !== undefined) {
      const parsed = Number(rulesConfig.maxFreeDeliveryKm);
      if (!isNaN(parsed) && parsed > 0) {
        maxFreeDeliveryKm = parsed;
      }
    }

    let freeDeliveryMinOrderAmount: number | undefined = undefined;
    if (rulesConfig.freeDeliveryMinOrderAmount !== undefined) {
      const parsed = Number(rulesConfig.freeDeliveryMinOrderAmount);
      if (!isNaN(parsed) && parsed > 0) {
        freeDeliveryMinOrderAmount = parsed;
      }
    }

    const enableAutoFreeDelivery =
      typeof rulesConfig.enableAutoFreeDelivery === 'boolean'
        ? rulesConfig.enableAutoFreeDelivery
        : undefined;

    const highestMaxKm = sortedRules.reduce<number>((acc, rule) => {
      const value = typeof rule.maxKm === 'number' ? rule.maxKm : acc;
      return value > acc ? value : acc;
    }, 0);

    if (highestMaxKm > 0) {
      if (typeof maxClientDistanceKm !== 'number' || maxClientDistanceKm < highestMaxKm) {
        maxClientDistanceKm = highestMaxKm;
      }
    }

    const scopedConfigToSave: Record<string, any> = {
      rules: sortedRules,
      lastUpdated: serverTimestamp(),
    };

    if (groupId) {
      scopedConfigToSave.groupId = groupId;
    }
    if (typeof maxClientDistanceKm === 'number') {
      scopedConfigToSave.maxClientDistanceKm = maxClientDistanceKm;
    }
    if (typeof maxFreeDeliveryKm === 'number') {
      scopedConfigToSave.maxFreeDeliveryKm = maxFreeDeliveryKm;
    }
    if (typeof freeDeliveryMinOrderAmount === 'number') {
      scopedConfigToSave.freeDeliveryMinOrderAmount = freeDeliveryMinOrderAmount;
    }
    if (typeof enableAutoFreeDelivery === 'boolean') {
      scopedConfigToSave.enableAutoFreeDelivery = enableAutoFreeDelivery;
    }

    const batch = writeBatch(db);
    const primaryDocRef = doc(db, 'delivery_rules', groupId);
    batch.set(primaryDocRef, scopedConfigToSave, { merge: true });

    await batch.commit();

    return {
      rules: sortedRules,
      maxClientDistanceKm,
      enableAutoFreeDelivery,
      freeDeliveryMinOrderAmount,
      maxFreeDeliveryKm,
      groupId,
    };
  } catch (error) {
    console.error('Erreur lors de la mise à jour des règles de livraison:', error);
    throw error;
  }
};
